<script setup lang="ts">
import { onMounted } from 'vue';
import { message } from 'ant-design-vue';
import { useRouter } from 'vue-router';
import { abandonNativeSignIn, completeCognitoSignInFromCallback } from '@/shared/auth/session';
import {
  nativeOnboardingMessage,
  requiresFreshNativeSignIn,
} from '@/shared/auth/nativeAuthFeedback';
import { resolvePostLoginRedirect } from '@/shared/auth/postLoginRedirect';
import { useAuthStore } from '@/shared/stores/authStore';
import { isNativeCognitoEnabled } from '@/shared/auth/cognitoFeatureFlag';
import { cognitoClient } from '@/shared/auth/cognitoClient';

const router = useRouter();
const authStore = useAuthStore();

onMounted(async () => {
  let version = isNativeCognitoEnabled ? cognitoClient.sessionVersion() : 0;
  try {
    const completing = completeCognitoSignInFromCallback(window.location.href);
    version = isNativeCognitoEnabled ? cognitoClient.sessionVersion() : 0;
    const { success, returnUrl } = await completing;
    if (isNativeCognitoEnabled && version !== cognitoClient.sessionVersion()) return;
    if (success) {
      await router.replace(resolvePostLoginRedirect(returnUrl, authStore.backendProfile?.roles));
      return;
    }

    if (isNativeCognitoEnabled) {
      const state = authStore.lifecycleStatus.onboardingState;
      if (requiresFreshNativeSignIn(state) && !(await abandonNativeSignIn(version))) return;
      message.error(nativeOnboardingMessage(state));
    }

    await router.replace(
      isNativeCognitoEnabled ? '/login' : { path: '/auth/error', query: { retry: 'profile' } },
    );
  } catch (error) {
    if (isNativeCognitoEnabled && version !== cognitoClient.sessionVersion()) return;
    if (isNativeCognitoEnabled) {
      if (!(await abandonNativeSignIn(version))) return;
      message.error('We could not complete sign in. Please sign in again.');
      await router.replace('/login');
      return;
    }
    console.error('Cognito auth callback failed:', error);
    await router.replace({ path: '/auth/error', query: { retry: 'false' } });
  }
});
</script>

<template>
  <div class="cognito-auth-callback" aria-busy="true" aria-live="polite" />
</template>

<style scoped>
.cognito-auth-callback {
  min-height: 40vh;
}
</style>
